import { useLayoutEffect, useRef, useState, type MouseEvent } from 'react'

/**
 * Dashboard.amChartSimpleLine din opteemum.js / proactivitee.js, ca SVG.
 * AmCharts 3 serial light: aria #EAEAEA 0.33, linie groasa 2, umplere 0.1,
 * gloante albe cu bordura din culoarea liniei, vreo zece etichete pe X
 * (labelFunction: pas = ceil(n/10)), balonul "$unit : **value**".
 *
 * De ce nu AmCharts: ruleaza pe jQuery, iar v4 nu il are. Un grafic de o
 * serie nu are nevoie de biblioteca; are nevoie sa arate la fel.
 */

export interface SimpleLineChartProps {
  points: { name: string; value: number }[]
  unit: string
  color: string
  height?: number
}

const FONT = 'Verdana, Arial, Helvetica, sans-serif'
const PAD = { top: 16, right: 14, bottom: 28, left: 44 }

export function labelStep(count: number): number {
  return Math.max(1, Math.ceil(count / 10))
}

export function SimpleLineChart({ points, unit, color, height = 150 }: SimpleLineChartProps) {
  const box = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [hover, setHover] = useState<number | null>(null)

  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const measure = () => setWidth(el.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const n = points.length
  const plotLeft = PAD.left
  const plotRight = Math.max(plotLeft + 1, width - PAD.right)
  const plotTop = PAD.top
  const plotBottom = height - PAD.bottom
  const plotWidth = Math.max(1, plotRight - plotLeft)
  const plotHeight = Math.max(1, plotBottom - plotTop)
  const max = Math.max(1, ...points.map((p) => p.value))
  const x = (i: number) => plotLeft + (n <= 1 ? plotWidth / 2 : (i / (n - 1)) * plotWidth)
  const y = (v: number) => plotBottom - (v / max) * plotHeight
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(2)} ${y(p.value).toFixed(2)}`).join(' ')
  const area = n
    ? `${line} L${x(n - 1).toFixed(2)} ${plotBottom} L${x(0).toFixed(2)} ${plotBottom} Z`
    : ''
  const step = labelStep(n)

  const onMove = (event: MouseEvent<SVGSVGElement>) => {
    if (!n) return
    const rect = event.currentTarget.getBoundingClientRect()
    const px = event.clientX - rect.left
    let best = 0
    for (let i = 1; i < n; i++) if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i
    setHover(best)
  }

  const tip = hover === null || !points[hover] ? null : { i: hover, ...points[hover], x: x(hover), y: y(points[hover].value) }

  return (
    <div ref={box} className="amcharts-simple-line" style={{ width: '100%', height, position: 'relative' }}>
      {width > 0 && (
        <svg width={width} height={height} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
          <rect x={0} y={0} width={width} height={height} fill="#FFFFFF" />
          <rect
            x={plotLeft}
            y={plotTop}
            width={plotWidth}
            height={plotHeight}
            fill="#EAEAEA"
            fillOpacity={0.33}
          />
          <path d={`M${plotLeft} ${plotBottom + 0.5} L${plotRight} ${plotBottom + 0.5}`} stroke="#999" strokeWidth={1} opacity={0.3} />
          {n > 0 && (
            <>
              <path d={area} fill={color} fillOpacity={0.1} />
              <path d={line} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
              {points.map((p, i) => (
                <circle
                  key={i}
                  cx={x(i)}
                  cy={y(p.value)}
                  r={hover === i ? 5 : 4}
                  fill="#FFFFFF"
                  stroke={color}
                  strokeWidth={1}
                />
              ))}
              {points.map((p, i) =>
                i % step === 0 ? (
                  <text
                    key={`x${i}`}
                    x={x(i)}
                    y={plotBottom + 16}
                    textAnchor="middle"
                    style={{ fill: '#555', fontSize: 11, fontFamily: FONT }}
                  >
                    {p.name}
                  </text>
                ) : null,
              )}
            </>
          )}
          {tip && (
            <g style={{ pointerEvents: 'none' }}>
              <rect x={Math.min(tip.x + 8, plotRight - 110)} y={Math.max(4, tip.y - 36)} width={108} height={28} rx={2} fill="rgba(255,255,255,0.92)" stroke={color} />
              <text
                x={Math.min(tip.x + 8, plotRight - 110) + 8}
                y={Math.max(4, tip.y - 36) + 18}
                style={{ fill: '#333', fontSize: 12, fontFamily: FONT }}
              >
                {unit} : <tspan style={{ fontWeight: 'bold' }}>{tip.value}</tspan>
              </text>
            </g>
          )}
        </svg>
      )}
    </div>
  )
}
